import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const WordCloud = () => {
  const [words, setWords] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/stats/word-cloud", { params: { limit: 50 } });
        if (!cancel) setWords(data.words || []);
      } catch {
        if (!cancel) setWords([]);
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (!words.length) return null;

  const counts = words.map((w) => w.count);
  const max = Math.max(...counts);
  const min = Math.min(...counts);

  // deterministic pseudo-random for stable rotations on each render
  const seeded = (i, salt = 0) => {
    const x = Math.sin((i + 1) * 9301 + salt) * 43758.5453;
    return x - Math.floor(x);
  };

  return (
    <section data-testid="word-cloud-section" className="relative bg-[#f2efe9] border-t border-b border-[#e5e1d8]">
      <div className="bg-[#1a1918] text-[#f9f8f6] py-10 sm:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#d4b886] mb-3">Ricorrenze</p>
            <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-none font-light text-[#f9f8f6]">
              Le parole che tornano
            </h2>
          </div>
          <p className="font-serif italic text-base sm:text-lg text-[#f9f8f6]/80 max-w-sm leading-relaxed">
            Più la parola è grande, più attraversa il diario.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div
          data-testid="word-cloud"
          className="flex flex-wrap items-baseline justify-center gap-x-5 sm:gap-x-7 gap-y-1 sm:gap-y-2"
          style={{ minHeight: "320px" }}
        >
          {words.map((w, i) => {
            const ratio = max === min ? 0.6 : (w.count - min) / (max - min);
            const fontSize = 0.95 + ratio * 3.6; // rem
            const opacity = 0.35 + ratio * 0.65;
            const rotate = (seeded(i) - 0.5) * 8; // -4..+4 deg
            const isAccent = seeded(i, 7) > 0.82;
            const weightVar = ratio > 0.6 ? 500 : 400;
            const italic = seeded(i, 13) > 0.6;
            return (
              <span
                key={w.word}
                data-testid={`word-${w.word}`}
                title={`${w.word} — ${w.count}`}
                className={`font-serif leading-none transition-colors duration-300 cursor-default ${italic ? "italic" : ""}`}
                style={{
                  fontSize: `${fontSize}rem`,
                  opacity,
                  transform: `rotate(${rotate.toFixed(2)}deg)`,
                  display: "inline-block",
                  color: isAccent ? "#c5a873" : "#1a1918",
                  fontWeight: weightVar,
                  letterSpacing: "-0.01em",
                  padding: "0.1em 0.05em",
                }}
              >
                {w.word}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WordCloud;
